import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import MainNav from "@/components/MainNav";
import { getCheckoutStatus } from "@/lib/api";
import { FaCheckCircle, FaSpinner, FaTimesCircle } from "react-icons/fa";
import { Button } from "@/components/ui/button";

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const res = await getCheckoutStatus(sessionId);
        setStatus(res.data);
        
        // Clear cart on successful payment
        if (res.data.payment_status === "paid") {
          localStorage.removeItem("cart");
          window.dispatchEvent(new Event("cartUpdated"));
        }
      } catch (error) {
        console.error("Error checking status:", error);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, [sessionId]);

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount / 100); // Stripe amounts are in pence
  };

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-success-page">
      <MainNav />

      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          {loading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-20"
            >
              <FaSpinner className="w-16 h-16 mx-auto text-zinc-500 animate-spin mb-4" />
              <p className="text-zinc-400">Confirming your order...</p>
            </motion.div>
          ) : status?.payment_status === "paid" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-12"
            >
              <FaCheckCircle className="w-24 h-24 mx-auto text-green-500 mb-6" />
              <h1 className="text-3xl md:text-4xl font-syne font-bold text-white mb-4">
                Thank You!
              </h1>
              <p className="text-xl text-zinc-300 mb-2">
                Your order has been confirmed.
              </p>
              <p className="text-zinc-500 mb-8">
                We'll send you an email confirmation shortly.
              </p>
              
              {status.amount_total && (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-8 inline-block">
                  <p className="text-sm text-zinc-400 mb-1">Order Total</p>
                  <p className="text-2xl font-bold text-white">{formatPrice(status.amount_total)}</p>
                </div>
              )}

              <div className="space-y-4">
                <Link to="/shop">
                  <Button className="bg-white text-black hover:bg-zinc-200 rounded-full px-8">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-12"
            >
              <FaTimesCircle className="w-24 h-24 mx-auto text-red-500 mb-6" />
              <h1 className="text-3xl font-syne font-bold text-white mb-4">
                Payment Not Completed
              </h1>
              <p className="text-zinc-400 mb-8">
                Your payment was not completed. Please try again.
              </p>
              <Link to="/shop/cart">
                <Button className="bg-white text-black hover:bg-zinc-200 rounded-full px-8">
                  Return to Cart
                </Button>
              </Link>
            </motion.div>
          )}
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-600 text-xs">© 2025 Underplay. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
